import {
  ApolloClient,
  DocumentNode,
  NormalizedCacheObject,
} from "@apollo/client";
import { Collection, Model } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import {
  EntityNode,
  StockCount,
  StockCountItem,
  StockCountLine,
} from "@towersystems/roam-common/lib/generated-types";
import { client } from "../../../../apollo";
import StockCountModel from "../../../../utilities/data/model/StockCount";
import StockCountLineModel from "../../../../utilities/data/model/StockCountLine";
import StockCountItemModel from "../../../../utilities/data/model/StockCountItem";
import { STOCK_COUNT_LINES } from "../../../../utilities/stock-count";

type ExtendedOptions = {
  isUpdate?: boolean;
};

export function getStockCountLinesVariables(stockCountId: string) {
  return {
    variables: {
      stockCountId: stockCountId,
    },
  };
}

export function prepareStockCountEntity(
  newEntity: StockCountModel,
  fetchedEntity: StockCount
) {
  newEntity._raw.id = fetchedEntity.id;
  newEntity.name = fetchedEntity.name;
  newEntity.retailerStocktakeId = fetchedEntity.retailerStocktakeId ?? null;
  newEntity.status = fetchedEntity.status;
}

export function prepareStockCountLineEntity(
  newEntity: StockCountLineModel,
  fetchedEntity: StockCountLine,
  extendedOptions?: ExtendedOptions
) {
  if (!extendedOptions?.isUpdate) {
    newEntity._raw.id = fetchedEntity.id;
    newEntity.stockCount.id = fetchedEntity.stockCountId;
    newEntity.product.id = fetchedEntity.productId || null;
    newEntity.barcode = fetchedEntity.barcode;
  }
  newEntity.name = fetchedEntity.name || null;
  newEntity.initialExpected = fetchedEntity.initialExpected || null;
  newEntity.expected = fetchedEntity.expected || null;
  newEntity.updatedAt = fetchedEntity.updatedAt;
  newEntity.deletedAt = fetchedEntity.deletedAt;
  newEntity.createdAt = fetchedEntity.createdAt;
}

export function prepareCountItemEntity(
  newEntity: StockCountItemModel,
  fetchedEntity: StockCountItem,
  extendedOptions?: ExtendedOptions
) {
  if (!extendedOptions?.isUpdate) {
    newEntity.stockCountLine.id = fetchedEntity.lineId;
  }
  newEntity.status = fetchedEntity.status;
  newEntity.counted = fetchedEntity.counted;
  newEntity.countedAt = fetchedEntity.countedAt;
  newEntity.remoteId = fetchedEntity.id;

  newEntity.updatedAt = fetchedEntity.updatedAt;
  newEntity.deletedAt = fetchedEntity.deletedAt;
  newEntity.createdAt = fetchedEntity.createdAt;
}

export function useGetChangedItemsByStockCountId() {
  async function getChangedItemsByStockCountId(
    stockCountId: string,
    after?: Date
  ): Promise<StockCountLine[]> {
    const items = pageQuery(
      client,
      "stockCountLines",
      STOCK_COUNT_LINES,
      after,
      {
        stockCountId: stockCountId,
      }
    );

    return items;
  }

  return { getChangedItemsByStockCountId };
}

export function useCreateOrUpdateEntity() {
  const database = useDatabase();

  async function createOrUpdateEntity<T extends EntityNode, U extends Model>(
    table: string,
    entity: T,
    prepareEntity: (
      model: U,
      entity: T,
      extendedOptions?: { isUpdate?: boolean }
    ) => void,
    extendedOptions?: {
      resolver?: (
        entity: T,
        databaseCollection: Collection<U>
      ) => Promise<U | undefined>;
    }
  ): Promise<U> {
    const collection = database.collections.get<U>(table);

    const target = extendedOptions?.resolver
      ? await extendedOptions.resolver(entity, collection)
      : await collection.find(entity.id).catch(() => undefined);

    if (!target) {
      await database.write(async () => {
        await database.collections
          .get<U>(table)
          .create((createdEntity) => prepareEntity(createdEntity, entity));
      });
    } else {
      await database.write(async () => {
        await target.update((updatedEntity) =>
          prepareEntity(updatedEntity, entity, {
            isUpdate: true,
          })
        );
      });
    }

    const res = extendedOptions?.resolver
      ? await extendedOptions.resolver(entity, collection)
      : await collection.find(entity.id).catch(() => undefined);

    if (!res) {
      throw new Error(`Failed to create or update ${table} entity`);
    }

    return res;
  }

  return { createOrUpdateEntity };
}

const pageQuery = async (
  client: ApolloClient<NormalizedCacheObject>,
  key: string,
  query: DocumentNode,
  after: Date | undefined,
  additionalVariables?: any
) => {
  const limit = 1000;
  let page = 1;
  let total = limit + 1;
  const r = [];
  while (page <= Math.ceil(total / limit)) {
    const { data } = await client.query({
      query,
      variables: {
        options: {
          limit,
          page,
          sort: { updatedAt: `DESC` },
          filter: {
            ...(after ? { updatedAt: { after: after } } : {}),
          },
        },
        ...(additionalVariables || {}),
      },
    });

    if (data[key].items.length > 0) {
      r.push(...data[key].items);
    }
    total = data[key]?.meta?.totalItems;
    page++;
  }
  return r;
};
